/**
 * Utilidades comunes de formateo
 * Una sola fuente de verdad para formateos
 */

import { format as dateFnsFormat } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { getActiveCountryConfig } from "@/config/countries";
import { getActiveLanguage } from "@/config/i18n/activeLanguage.service";

const getActiveDateLocale = () => {
  switch (getActiveLanguage()) {
    case "en":
      return enUS;
    case "pt":
      return ptBR;
    default:
      return es;
  }
};

/**
 * Formatea números según el país activo del negocio.
 * Criterio único de la app: el símbolo va pegado al número (`$122.559.044`),
 * sin el espacio que algunos locales insertan entre ambos.
 */
export const formatCurrency = (value: number): string => {
  const country = getActiveCountryConfig();
  return new Intl.NumberFormat(country.locale, {
    style: "currency",
    currency: country.currency.code,
    minimumFractionDigits: country.currency.decimalPlaces,
    maximumFractionDigits: country.currency.decimalPlaces,
  })
    .formatToParts(value)
    .filter((part) => !(part.type === "literal" && part.value.trim() === ""))
    .map((part) => part.value)
    .join("");
};

export const formatMoney = formatCurrency;

/** Número con separador de miles colombiano (4.500, 90.000). */
export const fmtCop = (value: number): string =>
  Math.round(value).toLocaleString("es-CO");

/** Monto con símbolo $ y locale es-CO. */
export const fmtCopSymbol = (value: number): string => `$${fmtCop(value)}`;

/**
 * Formato estándar de la app: "Jue, 2 de jul 2026"
 * Proporciona una sola fuente de verdad para fechas con día de la semana.
 */
export const formatStandardDate = (
  date: DateInput,
  options?: { capitalize?: boolean; showYear?: boolean },
): string => {
  const { capitalize = true, showYear = true } = options || {};
  const dateObj = safeDate(date);
  if (!dateObj || isNaN(dateObj.getTime())) return "—";

  const language = getActiveLanguage();
  const formatStr =
    language === "es"
      ? showYear
        ? "EEE, d 'de' MMM yyyy"
        : "EEE, d 'de' MMM"
      : showYear
        ? "EEE, d MMM yyyy"
        : "EEE, d MMM";
  let formatted = dateFnsFormat(dateObj, formatStr, {
    locale: getActiveDateLocale(),
  });

  // Limpiar puntos que date-fns agrega en abreviaturas de español (ej: "jue.")
  formatted = formatted.replace(/\./g, "");

  // Capitalización forzada para día y mes: "Jue, 2 de Jul 2026"
  // Dividimos la cadena para encontrar el mes (la palabra después de "de ")
  if (capitalize) {
    const parts = formatted.split(" ");
    const processedParts = parts.map((part, index) => {
      // Capitalizar la primera parte (el día: Jue,)
      if (index === 0) return part.charAt(0).toUpperCase() + part.slice(1);
      // Capitalizar el mes en formatos latinos (palabra posterior a "de").
      if (index > 0 && parts[index - 1].toLowerCase() === "de") {
        return part.charAt(0).toUpperCase() + part.slice(1);
      }
      return part;
    });
    return processedParts.join(" ");
  }
  return formatted;
};

/**
 * Eje Y de gráficos y leyendas (evita valores como "$80000k" en montos
 * grandes). Recorta el decimal cuando es 0: 1.5M → "$1.5M", 2.0M → "$2M".
 */
export const formatChartAxisCop = (val: number): string => {
  if (!Number.isFinite(val)) return "$0";
  const trimZero = (text: string): string => text.replace(/\.0$/, "");
  const abs = Math.abs(val);
  if (abs >= 1e9) return `$${trimZero((val / 1e9).toFixed(1))}B`;
  if (abs >= 1e6) return `$${trimZero((val / 1e6).toFixed(1))}M`;
  if (abs >= 1e3) return `$${Math.round(val / 1e3)}k`;
  return `$${Math.round(val)}`;
};

export type DateInput =
  | Date
  | string
  | { _seconds: number; seconds?: number }
  | { seconds: number; _seconds?: number }
  | null
  | undefined;

/**
 * Formatea fechas con locale español
 * Soporta Date, ISO String y Firestore Timestamps
 */
export const formatDate = (
  date: DateInput,
  formatStr: string = "d 'de' MMMM, yyyy",
): string => {
  if (!date) return "N/A";
  let dateObj: Date;

  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === "string") {
    dateObj = new Date(date);
  } else if (typeof date === "object" && "_seconds" in date && date._seconds) {
    dateObj = new Date(date._seconds * 1000);
  } else if (typeof date === "object" && "seconds" in date && date.seconds) {
    dateObj = new Date(date.seconds * 1000);
  } else {
    return "N/A";
  }

  if (isNaN(dateObj.getTime())) return "N/A";

  return dateFnsFormat(dateObj, formatStr, { locale: getActiveDateLocale() });
};

/**
 * Formatea hora con AM/PM
 * Soporta Date, ISO String y Firestore Timestamps
 */
export const formatTime = (date: DateInput): string => {
  if (!date) return "N/A";
  let dateObj: Date;

  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === "string") {
    dateObj = new Date(date);
  } else if (typeof date === "object" && "_seconds" in date && date._seconds) {
    dateObj = new Date(date._seconds * 1000);
  } else if (typeof date === "object" && "seconds" in date && date.seconds) {
    dateObj = new Date(date.seconds * 1000);
  } else {
    return "Hora inválida";
  }

  return dateFnsFormat(dateObj, "p", { locale: getActiveDateLocale() })
    .replace("a. m.", "AM")
    .replace("p. m.", "PM");
};

/**
 * Formatea hora en formato militar 24h "HH:mm" (ej. 08:05, 17:30).
 * Soporta Date, ISO String y Firestore Timestamps.
 */
export const formatTime24 = (date: DateInput): string => {
  if (!date) return "N/A";
  let dateObj: Date;

  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === "string") {
    dateObj = new Date(date);
  } else if (typeof date === "object" && "_seconds" in date && date._seconds) {
    dateObj = new Date(date._seconds * 1000);
  } else if (typeof date === "object" && "seconds" in date && date.seconds) {
    dateObj = new Date(date.seconds * 1000);
  } else {
    return "Hora inválida";
  }

  if (isNaN(dateObj.getTime())) return "Hora inválida";

  return dateFnsFormat(dateObj, "HH:mm", { locale: getActiveDateLocale() });
};

/** Fecha del comprobante de venta (sin hora). */
export const formatTicketSaleDate = (date: DateInput): string =>
  formatDate(date, "d/M/yyyy");

/** Hora del comprobante de venta (sin fecha). */
export const formatTicketSaleTime = (date: DateInput): string =>
  formatTime(date);

/**
 * Convierte una fecha a string `YYYY-MM-DD` en hora local.
 * Maneja Date, ISO-8601, timestamps (ms) y strings "YYYY-MM-DD".
 * Los strings de solo fecha se devuelven tal cual para evitar desplazamientos
 * de zona horaria. Útil para comparar fechas con el filtro de calendario.
 */
export const toLocalIsoDate = (
  input: Date | string | number | null | undefined,
): string | null => {
  if (input == null) return null;
  if (typeof input === "string" && /^\d{4}-\d{2}-\d{2}$/.test(input))
    return input;
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/**
 * Compara dos fechas comparando su representación local `YYYY-MM-DD`.
 * Devuelve false si alguna de las dos no es una fecha válida.
 */
export const isSameLocalDate = (
  a: Date | string | number | null | undefined,
  b: Date | string | number | null | undefined,
): boolean => {
  const da = toLocalIsoDate(a);
  const db = toLocalIsoDate(b);
  return da != null && db != null && da === db;
};

/**
 * Convierte hora militar "HH:mm" a formato 12h "h:mm AM/PM"
 * Ejemplo: "18:00" → "6:00 PM", "09:00" → "9:00 AM"
 */
export const formatHour24To12 = (hora: string | undefined | null): string => {
  if (!hora) return "—";
  const [h, m] = hora.split(":").map(Number);
  if (isNaN(h) || isNaN(m)) return hora;
  const suffix = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${suffix}`;
};

/**
 * Formatea fecha y hora completa con día, mes, año, hora AM/PM
 * Ejemplo: "Vie, 4 jul 2026, 10:53 AM"
 */
export const formatFullDateTime = (date: Date | string): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  let formatted = dateFnsFormat(dateObj, "EEE, d MMM yyyy, h:mm a", {
    locale: es,
  });

  // Limpiar puntos y formatear AM/PM
  formatted = formatted
    .replace(/\./g, "")
    .replace("a. m.", "AM")
    .replace("p. m.", "PM");

  // Capitalizar primera letra (el día: "vie" -> "Vie")
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

/**
 * Formatea ISO `YYYY-MM-DD` al estilo estándar: "Jue, 2 de jul 2026".
 * Se usa principalmente en RecordCount y resúmenes de filtros.
 */
const formatPickerDateLabel = (iso: string): string => {
  if (!iso) return "";
  // Forzamos el parseo local para evitar desfases de zona horaria con el string ISO
  const [year, month, day] = iso.split("-").map(Number);
  const dateObj = new Date(year, month - 1, day);
  return formatStandardDate(dateObj);
};

/**
 * Rango de fechas para filtros (desde/hasta) en el mismo estilo del DatePicker.
 */
export const formatPickerDateRangeLabel = (
  from?: string,
  to?: string,
): string => {
  const start = formatPickerDateLabel(from ?? "");
  const end = formatPickerDateLabel(to ?? "");
  if (start && end && start !== end) return `${start} — ${end}`;
  return start || end;
};

/** Fecha local de hoy en ISO `YYYY-MM-DD` (sin desfase UTC). */
export const getTodayIso = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/**
 * Convierte un valor a Date seguro (sin NaN)
 */
export const safeDate = (value: DateInput): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return isNaN(value.getTime()) ? new Date() : value;
  if (typeof value === "string") {
    const d = new Date(value);
    return isNaN(d.getTime()) ? new Date() : d;
  }
  if ("_seconds" in value && value._seconds)
    return new Date(value._seconds * 1000);
  if ("seconds" in value && value.seconds)
    return new Date(value.seconds * 1000);
  return new Date();
};

/**
 * Formatea porcentaje
 */
export const formatPercent = (value: number, decimals: number = 1): string => {
  return `${value.toFixed(decimals)}%`;
};

/**
 * Porcentaje con separador decimal colombiano (12,5%), para textos de la UI
 * en español. `formatPercent` conserva el punto decimal (contratos/exportes).
 */
export const formatPercentCop = (value: number, decimals: number = 1): string =>
  `${value.toLocaleString("es-CO", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}%`;

/**
 * Formatea un número con separadores de miles (locale es-CO)
 */
export const formatNumberInput = (
  value: number | string | undefined | null,
): string => {
  if (value === undefined || value === null || value === "") return "";
  // Eliminamos cualquier carácter que no sea dígito para refallear el formateo
  const raw = String(value).replace(/\D/g, "");
  const num = Number(raw);
  if (isNaN(num)) return "";
  return num.toLocaleString("es-CO");
};

/**
 * Elimina separadores de miles (puntos) y cualquier carácter no numérico
 * para obtener el valor numérico raw en string.
 */
export const parseNumberInput = (value: string | undefined | null): string => {
  if (!value) return "0";
  return value.replace(/\D/g, "") || "0";
};

/**
 * Normaliza la entrada de un campo de dinero en vivo (fuente única para
 * inputs de montos con/sin decimales del país activo).
 * - `fractionDigits <= 0`: solo dígitos (conserva "" para permitir campo vacío).
 * - `fractionDigits > 0`: "1.234,5" → "1234.5" limitando decimales al país.
 */
export const normalizeMoneyInput = (
  value: string,
  fractionDigits: number,
): string => {
  if (fractionDigits <= 0) {
    return value.replace(/\D/g, "");
  }
  const normalized = value.replace(",", ".");
  const [integerPart = "", ...decimalParts] = normalized.split(".");
  const integer = integerPart.replace(/\D/g, "");
  if (decimalParts.length === 0) return integer;
  const decimals = decimalParts.join("").replace(/\D/g, "");
  return `${integer || "0"}.${decimals.slice(0, fractionDigits)}`;
};

type FirestoreDateLike = {
  seconds?: number;
  _seconds?: number;
  toDate?: () => Date;
};

/**
 * Convierte cualquier representación de fecha de Firestore a un objeto Date.
 * Soporta: número (ms), string ISO, objeto Firestore Timestamp ({ seconds, _seconds }), Date nativo.
 */
export const firestoreToDate = (
  date: number | string | Date | FirestoreDateLike | null | undefined,
): Date => {
  if (!date) return new Date();
  if (date instanceof Date) return date;
  if (typeof date === "number") return new Date(date);
  if (typeof date === "string") return new Date(date);

  const d = date as FirestoreDateLike;
  if (typeof d.toDate === "function") return d.toDate();
  const seconds = d.seconds ?? d._seconds;
  if (typeof seconds === "number") return new Date(seconds * 1000);

  return new Date();
};

/**
 * Convierte cualquier representación de fecha de Firestore a string legible.
 * Soporta: número (ms), string ISO, objeto Firestore Timestamp, Date nativo.
 */
export const formatFirestoreDate = (
  date: number | string | Date | FirestoreDateLike | null | undefined,
): string | null => {
  if (!date) return null;
  try {
    const locale = "es-ES";
    const opts: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };

    if (typeof date === "number")
      return new Date(date).toLocaleString(locale, opts);
    if (typeof date === "string")
      return new Date(date).toLocaleString(locale, opts);
    if (date instanceof Date) return date.toLocaleString(locale, opts);

    const d = date as FirestoreDateLike;
    if (typeof d.toDate === "function")
      return d.toDate().toLocaleString(locale, opts);
    const seconds = d.seconds ?? d._seconds;
    if (typeof seconds === "number")
      return new Date(seconds * 1000).toLocaleString(locale, opts);

    return null;
  } catch {
    return null;
  }
};

/**
 * Convierte el nombre de un producto a slug URL-safe.
 * Ej: "Agua Cristal 600ml" → "agua-cristal-600ml"
 * Soporta tildes, ñ y caracteres especiales comunes.
 */
export const toProductSlug = (nombre: string): string =>
  nombre
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // quitar tildes
    .replace(/ñ/g, "n")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

/**
 * Fecha ISO segura para nombres de archivo.
 * Ej: "2026-07-14" o "2026-07-14_10-08" si includeTime=true.
 * Reutilizable para PDFs, imágenes y cualquier exportación compartida.
 */
export const formatFilenameDate = (
  date: DateInput,
  options?: { includeTime?: boolean },
): string => {
  const dateObj = safeDate(date);
  if (isNaN(dateObj.getTime())) return "sin-fecha";
  const formatted = dateFnsFormat(dateObj, "yyyy-MM-dd");
  if (options?.includeTime) {
    return `${formatted}_${dateFnsFormat(dateObj, "HH-mm")}`;
  }
  return formatted;
};
